/**
 * Atomic Design showcase — /atomic-design
 *
 * HR-or-above access only. Mirrors the layout and primitive inventory of
 * prototype/Atomic Design.html so reviewers can verify LMS parity at a
 * single URL without spelunking source code.
 *
 * Structure mirrors Brad Frost's five atomic stages:
 *   0. Tokens    — colour, typography, spacing, leave-types, calendar-states
 *   1. Atoms     — Icon, IlmuMark, Button, Input, Label, Badge, Avatar, Kbd, Divider
 *   2. Molecules — SearchField, FormField, IlmuChip, UserChip, NavItem, TabBar,
 *                  Stat, CapacityBar, Toast, Notification, CalendarCell, Breadcrumb, ToolCall
 *   3. Organisms — Sidebar, TopBar, ApprovalsTable, ConflictResolver, AIPanel,
 *                  Calendar, BalanceRing, Card, CommandPalette, Modal
 *   4. Templates — Dashboard, List+Detail, Form/Wizard, Detail, AdminIndex
 */

import { PageShell, Toc } from './atomic-design/shared'
import { TokensSection } from './atomic-design/TokensSection'
import { AtomsSection } from './atomic-design/AtomsSection'
import { MoleculesSection } from './atomic-design/MoleculesSection'
import { OrganismsSection } from './atomic-design/OrganismsSection'
import { TemplatesSection } from './atomic-design/TemplatesSection'

export function AtomicDesignPage(): JSX.Element {
  return (
    <PageShell>
      <Toc />

      <main style={{ minWidth: 0, padding: '32px 48px 120px' }}>
        {/* ── HERO ── */}
        <header
          style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 36,
            paddingBottom: 32,
            borderBottom: '1px solid var(--border)',
            marginBottom: 36,
            alignItems: 'end',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--text-3)',
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              Component Library · Leave Hub Live
            </div>
            <h1
              style={{
                fontSize: 42,
                fontWeight: 600,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                margin: '0 0 14px',
              }}
            >
              Build from the inside&nbsp;out.
            </h1>
            <p
              style={{ fontSize: 15, color: 'var(--text-2)', maxWidth: '46ch', margin: 0, lineHeight: 1.55 }}
            >
              Every Leave Hub primitive, rendered live. Matches the{' '}
              <strong>Lumen Atomic Design</strong> spec at each of the five stages.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 18,
                marginTop: 22,
                fontSize: 11.5,
                color: 'var(--text-3)',
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {[
                { n: '5', l: 'stages' },
                { n: '9', l: 'atoms' },
                { n: '13', l: 'molecules' },
                { n: '10', l: 'organisms' },
                { n: '5', l: 'templates' },
              ].map(({ n, l }) => (
                <span key={l}>
                  <strong
                    style={{
                      color: 'var(--text)',
                      fontWeight: 600,
                      display: 'block',
                      fontSize: 18,
                      fontFamily: 'var(--font-sans)',
                      textTransform: 'none',
                      letterSpacing: '-0.01em',
                      marginBottom: 2,
                    }}
                  >
                    {n}
                  </strong>
                  {l}
                </span>
              ))}
            </div>
          </div>

          {/* Atomic ladder */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { s: 1, stage: 'atoms', label: 'Atoms', sub: 'Color, type, icons, buttons, inputs', n: '14', color: 'var(--atom)' },
              { s: 2, stage: 'molecules', label: 'Molecules', sub: 'Form field, search, capacity bar', n: '13', color: 'var(--mol)' },
              { s: 3, stage: 'organisms', label: 'Organisms', sub: 'Sidebar, calendar, AI panel', n: '11', color: 'var(--org)' },
              { s: 4, stage: 'templates', label: 'Templates', sub: 'Dashboard, detail, form, admin', n: '5', color: 'var(--tmpl)' },
            ].map(({ s, stage, label, sub, n, color }) => (
              <a
                key={s}
                href={`#${stage}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '24px 1fr auto',
                  gap: 14,
                  alignItems: 'center',
                  padding: '10px 14px',
                  borderRadius: 10,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'transform 0.16s, box-shadow 0.16s',
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateX(2px)'
                  ;(e.currentTarget as HTMLAnchorElement).style.boxShadow =
                    '0 1px 0 rgba(32,41,69,.04),0 1px 2px rgba(32,41,69,.04)'
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLAnchorElement).style.transform = 'none'
                  ;(e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none'
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'grid',
                    placeItems: 'center',
                    background: color,
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {String(s).padStart(2, '0')}
                </span>
                <span>
                  <b style={{ fontWeight: 600, fontSize: 13.5, letterSpacing: '-0.005em' }}>
                    {label}
                  </b>
                  <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 1 }}>{sub}</div>
                </span>
                <em
                  style={{
                    fontStyle: 'normal',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10.5,
                    color: 'var(--text-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {n}
                </em>
              </a>
            ))}
          </div>
        </header>

        <TokensSection />
        <AtomsSection />
        <MoleculesSection />
        <OrganismsSection />
        <TemplatesSection />

        {/* Footer */}
        <footer
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 32,
            borderTop: '1px solid var(--border)',
            fontSize: 11.5,
            color: 'var(--text-3)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span>Lumen · Atomic Design · Leave Hub live parity showcase</span>
          <span>HR+ access only</span>
        </footer>
      </main>
    </PageShell>
  )
}
